import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Achievement, AchievementTier } from './catalog';

const KEYFRAMES_ID = 'pressto-achievement-toast-keyframes';

function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes pressto-achievement-toast-in {
      0%   { transform: translateX(120%) scale(0.92); opacity: 0; }
      12%  { transform: translateX(-4%)  scale(1.02); opacity: 1; }
      20%  { transform: translateX(0)    scale(1);    opacity: 1; }
      88%  { transform: translateX(0)    scale(1);    opacity: 1; }
      100% { transform: translateX(120%) scale(0.92); opacity: 0; }
    }
    @keyframes pressto-achievement-toast-icon-pop {
      0%   { transform: scale(1)   rotate(0deg); }
      30%  { transform: scale(1.25) rotate(-10deg); }
      55%  { transform: scale(0.92) rotate(8deg); }
      80%  { transform: scale(1.06) rotate(-3deg); }
      100% { transform: scale(1)    rotate(0deg); }
    }
    @keyframes pressto-achievement-sparkle {
      0%   { opacity: 0; transform: scale(0.4); }
      50%  { opacity: 1; transform: scale(1.2); }
      100% { opacity: 0; transform: scale(1.6); }
    }
  `;
  document.head.appendChild(style);
}

const TIER_STYLES: Record<AchievementTier, { bg: string; border: string; eyebrow: string }> = {
  bronze: { bg: '#FFF8E7', border: '#1e1e1e', eyebrow: '#A87100' },
  silver: { bg: '#EEF4FF', border: '#1e1e1e', eyebrow: '#5C4FB8' },
  gold:   { bg: '#FFFAD9', border: '#1e1e1e', eyebrow: '#B8860B' },
};

function Toast({ ach, index, onDismiss }: {
  ach: Achievement;
  index: number;
  onDismiss: () => void;
}) {
  // Hold for ~4s (matches the slide-in/hold/slide-out animation total).
  useEffect(() => {
    const t = window.setTimeout(onDismiss, 4500);
    return () => window.clearTimeout(t);
  }, [onDismiss]);

  const tier = TIER_STYLES[ach.tier];

  return (
    <div
      style={{
        position: 'fixed',
        top: `${24 + index * 100}px`,
        right: '24px',
        width: '360px',
        backgroundColor: tier.bg,
        border: `3px solid ${tier.border}`,
        borderRadius: '14px',
        boxShadow: '6px 6px 0px #1e1e1e',
        padding: '16px 20px 16px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        zIndex: 10000,
        animation: 'pressto-achievement-toast-in 4.5s cubic-bezier(.22,.61,.36,1) forwards',
        pointerEvents: 'auto',
        cursor: 'pointer',
      }}
      onClick={onDismiss}
    >
      <div
        style={{
          position: 'relative',
          flexShrink: 0,
          width: 60,
          height: 60,
          borderRadius: '50%',
          backgroundColor: '#FFFFFF',
          border: '2.5px solid #1e1e1e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            fontSize: 34,
            lineHeight: 1,
            display: 'inline-block',
            animation: 'pressto-achievement-toast-icon-pop 1.2s ease-out 0.25s',
          }}
          aria-hidden
        >
          {ach.icon}
        </span>
        {/* Sparkle accent for silver/gold tiers */}
        {(ach.tier === 'silver' || ach.tier === 'gold') && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              fontSize: 18,
              animation: 'pressto-achievement-sparkle 1s ease-out 0.6s',
              pointerEvents: 'none',
            }}
          >
            ✨
          </span>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 800,
            fontSize: '0.6rem',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: tier.eyebrow,
          }}
        >
          Achievement Unlocked
        </div>
        <div
          style={{
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            fontSize: '1.1rem',
            color: '#1e1e1e',
            lineHeight: 1.15,
          }}
        >
          {ach.name}
        </div>
        <div
          style={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: '0.78rem',
            color: '#444',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {ach.description}
        </div>
      </div>
    </div>
  );
}

interface AchievementToastProps {
  achievements: Achievement[];
  onDismiss: (id: string) => void;
}

/**
 * Top-right achievement toast renderer. Slides in from off-screen-right,
 * holds, slides back out. Stacks vertically when multiple unlock at once.
 *
 * Pure-render component: takes a list of currently-celebrating achievements
 * and an onDismiss callback. Owns NO state — the parent manages the queue.
 */
export default function AchievementToast({ achievements, onDismiss }: AchievementToastProps) {
  useEffect(() => {
    ensureKeyframes();
  }, []);

  // Stable identity per achievement so React keeps the same DOM node + animation
  // even when the array shifts (e.g., after a dismiss). Memoize the rendered list.
  const items = useMemo(() => achievements.slice(0, 5), [achievements]);

  if (typeof document === 'undefined' || items.length === 0) {
    return null;
  }

  return createPortal(
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {items.map((ach, i) => (
        <Toast key={ach.id} ach={ach} index={i} onDismiss={() => onDismiss(ach.id)} />
      ))}
    </div>,
    document.body,
  );
}
